import React from "react";
import { Helmet } from "react-helmet-async";
import {
    RouteSeo,
    SITE_NAME,
    DEFAULT_IMAGE,
    DEFAULT_IMAGE_ALT,
    DEFAULT_IMAGE_WIDTH,
    DEFAULT_IMAGE_HEIGHT,
    SITE_ORIGIN,
    canonicalUrl,
    localeOf,
    translationsOf,
} from "./routeSeo.ts";

interface SeoProps {
    entry: RouteSeo;
}

/** FAQ rich-result markup, shown under the listing in search results. */
function faqSchema(entry: RouteSeo) {
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        mainEntity: entry.faq!.map((item) => ({
            "@type": "Question",
            name: item.q,
            acceptedAnswer: { "@type": "Answer", text: item.a },
        })),
    };
}

/** Identifies the site itself, so search engines can attach the brand name. */
function websiteSchema() {
    return {
        "@context": "https://schema.org",
        "@type": "WebSite",
        name: SITE_NAME,
        url: SITE_ORIGIN,
    };
}

/** Home > page trail, which search results render in place of the raw URL. */
function breadcrumbSchema(entry: RouteSeo) {
    return {
        "@context": "https://schema.org",
        "@type": "BreadcrumbList",
        itemListElement: [
            { "@type": "ListItem", position: 1, name: "Home", item: SITE_ORIGIN },
            {
                "@type": "ListItem",
                position: 2,
                name: entry.h1 ?? entry.title,
                item: canonicalUrl(entry.slug),
            },
        ],
    };
}

/**
 * Sets per-route document head tags (title, description, canonical, Open Graph,
 * Twitter, structured data) at runtime. The build-time prerender script
 * (scripts/prerender.js) bakes the same values into the static HTML from the
 * same manifest, so crawlers and no-JS clients see identical metadata.
 *
 * **These updates are deferred to `requestAnimationFrame`, so they do not
 * happen in a tab that is not painting.** `react-helmet-async` commits
 * synchronously only when `defer` is false; otherwise `handleStateChangeOnClient`
 * queues `commitTagChanges` on a frame, and a hidden tab never gets one. Load a
 * page in a headless or offscreen browser and the head keeps the shell's title
 * with no `[data-rh]` tags at all - which looks exactly like Helmet being
 * broken, and is not. Measured: freezing `requestAnimationFrame` in jsdom
 * reproduces it precisely (0 tags, shell title), and restoring it gives 18 tags
 * and the manifest title.
 *
 * Nothing needs fixing for real visitors, and `defer={false}` is deliberately
 * not set: a page's first paint already carries the right head from the
 * prerendered HTML, including in a background tab, so the only thing waiting on
 * a frame is a client-side navigation - which the visitor is by definition
 * looking at. The note is here because the symptom is so convincing that it is
 * worth not diagnosing twice.
 */
export function Seo({ entry }: SeoProps) {
    const canonical = canonicalUrl(entry.slug);
    const image = SITE_ORIGIN + DEFAULT_IMAGE;
    const isHome = entry.slug === "/";
    // ADR 0051 §8: derived from the single `translationGroup` pairing rather
    // than a hand-maintained list, so the English page and its Spanish/French
    // alternates cannot name each other inconsistently.
    const alternates = translationsOf(entry);

    return (
        <Helmet htmlAttributes={{ lang: localeOf(entry) }}>
            <title>{entry.title}</title>
            <meta name="description" content={entry.description} />
            <link rel="canonical" href={canonical} />
            <link rel="alternate" hrefLang={localeOf(entry)} href={canonical} />
            {alternates.map((alternate) => (
                <link
                    key={alternate.slug}
                    rel="alternate"
                    hrefLang={localeOf(alternate)}
                    href={canonicalUrl(alternate.slug)}
                />
            ))}
            {entry.noindex && <meta name="robots" content="noindex, follow" />}

            <meta property="og:type" content="website" />
            <meta property="og:site_name" content={SITE_NAME} />
            <meta property="og:title" content={entry.title} />
            <meta property="og:description" content={entry.description} />
            <meta property="og:url" content={canonical} />
            <meta property="og:image" content={image} />
            <meta property="og:image:width" content={String(DEFAULT_IMAGE_WIDTH)} />
            <meta property="og:image:height" content={String(DEFAULT_IMAGE_HEIGHT)} />
            <meta property="og:image:alt" content={DEFAULT_IMAGE_ALT} />

            <meta name="twitter:card" content="summary_large_image" />
            <meta name="twitter:title" content={entry.title} />
            <meta name="twitter:description" content={entry.description} />
            <meta name="twitter:image" content={image} />
            <meta name="twitter:image:alt" content={DEFAULT_IMAGE_ALT} />

            {isHome && (
                <script type="application/ld+json">{JSON.stringify(websiteSchema())}</script>
            )}

            {!isHome && !entry.noindex && (
                <script type="application/ld+json">{JSON.stringify(breadcrumbSchema(entry))}</script>
            )}

            {entry.faq && entry.faq.length > 0 && (
                <script type="application/ld+json">{JSON.stringify(faqSchema(entry))}</script>
            )}
        </Helmet>
    );
}

export default Seo;
